import { useForm } from "@inertiajs/react"
import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { InputBase, Label, TextField } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { NativeSelect } from "@/components/ui/select"
import { TextArea } from "@/components/ui/textarea"
import { toFieldError } from "@/lib/utils"
import type {
  Attempt,
  CohortOption,
  DispositionOption,
  LogFormContact,
  ReviewContact,
} from "@/pages/admin/calls/types"

import { PRESSED_ECHO, REVIEW } from "./copy"

/*
 * The one form that submits a call: the manual log-a-call page (Phase 2) and
 * the review screen (Phase 3). Same fields, same server-side LogForm, one
 * Task. The review variant arrives pre-filled from the session and the memo,
 * shows the contact and the attempt as facts, and marks the disposition as
 * suggested — never silently committed.
 */
export interface LogCallFormValues {
  salesforce_contact_id: string
  disposition: string
  reached: string
  talk_minutes: string
  via_reception: boolean
  called_at: string
  notes: string
  next_step: string
  next_step_on: string
  cohort_name: string
  expected_term: string
  provider: string
  consent_method: string
  consent_lead_days: string
  preferred_term: string
  venue_notes: string
  [key: string]: string | boolean
}

/** The dispositions that ask the form for one more fact (design v3 §2.8.2). */
const ASKS: Record<
  string,
  "commitment" | "provider" | "consent" | "term" | "venue"
> = {
  "booked-verbal": "commitment",
  "other-provider": "provider",
  "consent-issue": "consent",
  "calendar-full": "term",
  logistics: "venue",
}

export const REACHED_LABELS: Record<string, string> = {
  human: "A person",
  machine: "Voicemail or a machine",
  not_sure: "Not sure",
  unreached: "Nobody",
}

/** What a disposition implies about who was reached, so the hand form pre-fills it. */
const reachedFor = (d: DispositionOption | undefined) => {
  if (!d) return "human"
  if (d.spoke) return "human"
  if (d.value === "voicemail") return "machine"
  return "unreached"
}

/** The browser's local time, as a datetime-local input wants it. */
export const toLocalInput = (iso: string) => {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const today = () => new Date().toISOString().slice(0, 10)

/** The empty form with the given values laid over it; an undefined value leaves the default. */
const withDefaults = (
  initial: Partial<LogCallFormValues>,
): LogCallFormValues => {
  const values = emptyValues()
  for (const [k, v] of Object.entries(initial))
    if (v !== undefined) values[k] = v
  return values
}

export const emptyValues = (): LogCallFormValues => ({
  salesforce_contact_id: "",
  disposition: "",
  reached: "human",
  talk_minutes: "0",
  via_reception: false,
  called_at: toLocalInput(new Date().toISOString()),
  notes: "",
  next_step: "",
  next_step_on: "",
  cohort_name: "",
  expected_term: "",
  provider: "",
  consent_method: "",
  consent_lead_days: "",
  preferred_term: "",
  venue_notes: "",
})

export function LogCallForm({
  action,
  paramKey,
  variant,
  initial,
  contacts,
  dispositions,
  reached_options,
  cohorts,
  terms,
  attempt,
  suggestedDisposition = null,
  lockedContact = null,
  attemptsFirst = false,
  notesPlaceholder,
  notesNote,
  submitLabel,
  cancelHref,
  disabled = false,
  footer,
}: {
  action: string
  paramKey: string
  variant: "hand" | "review"
  initial: Partial<LogCallFormValues>
  contacts: LogFormContact[]
  dispositions: DispositionOption[]
  reached_options: string[]
  cohorts: CohortOption[]
  terms: { value: string; label: string }[]
  attempt: Attempt
  suggestedDisposition?: string | null
  /** The contact the session dialled, shown as a fact rather than a choice. */
  lockedContact?: ReviewContact | null
  /** Attempt outcomes before the spoke ones: an unreached or machine session. */
  attemptsFirst?: boolean
  notesPlaceholder?: string
  /** A mono aside on the notes label: "seeded from the summary". */
  notesNote?: string
  submitLabel: string
  cancelHref?: string
  disabled?: boolean
  footer?: ReactNode
}) {
  const spoke = dispositions.filter((d) => d.spoke)
  const attempts = dispositions.filter((d) => !d.spoke)
  const { data, setData, post, processing, errors, transform } =
    useForm<LogCallFormValues>(withDefaults(initial))
  const baseError = (errors as Record<string, string | undefined>).base
  const chosen = dispositions.find((d) => d.value === data.disposition)
  const asks = chosen ? ASKS[chosen.value] : undefined
  const review = variant === "review"
  const spokeInert = review && data.reached !== "human"
  const cohortLabel = (k: CohortOption) =>
    `${k.name}${k.year_level_now ? ` (Year ${k.year_level_now})` : ""}${k.to_continue ? " · to continue" : ""}${k.graduated ? " · graduated" : ""}`
  const hintOf = (name: string) =>
    typeof errors[name] === "string" ? (errors[name] as string) : undefined
  const errorOf = (name: string) => toFieldError(hintOf(name))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (disabled) return
    transform((d) => ({ [paramKey]: d }))
    post(action)
  }

  const spokeOptions = spoke.map((d) => ({
    label: d.label,
    value: d.value,
    disabled: spokeInert,
  }))
  const attemptOptions = attempts.map((d) => ({
    label: `Attempt: ${d.label}`,
    value: d.value,
  }))
  const dispositionOptions = attemptsFirst
    ? [...attemptOptions, ...spokeOptions]
    : [...spokeOptions, ...attemptOptions]
  const dispositionHint =
    hintOf("disposition") ??
    (review
      ? suggestedDisposition && data.disposition === suggestedDisposition
        ? `Suggested from reached: ${REACHED_LABELS[data.reached]?.toLowerCase() ?? data.reached}. Change it if that is not what happened.`
        : spokeInert
          ? "Spoke dispositions need reached: a person."
          : "Spoken outcomes first, then the attempts that advance the sequence."
      : "Spoken outcomes first, then the attempts that advance the sequence.")

  return (
    <form onSubmit={submit} className="flex flex-col gap-5" id="log-call-form">
      {baseError && (
        <Notice tone="caution" role="alert">
          {baseError}
        </Notice>
      )}

      {review && lockedContact ? (
        <div className="flex flex-col gap-1">
          <span className="eyebrow">Contact</span>
          <span className="text-secondary text-sm">
            {lockedContact.name}
            {lockedContact.title ? ` — ${lockedContact.title}` : ""}
          </span>
        </div>
      ) : null}

      <div
        className={
          review && lockedContact
            ? "grid grid-cols-1 gap-4"
            : "grid grid-cols-1 gap-4 sm:grid-cols-2"
        }
      >
        {!(review && lockedContact) && (
          <NativeSelect
            label="Who you spoke to, or asked for"
            value={data.salesforce_contact_id}
            onChange={(e) => setData("salesforce_contact_id", e.target.value)}
            hint={hintOf("salesforce_contact_id")}
            disabled={disabled}
            options={[
              { label: "Nobody in particular (reception)", value: "" },
              ...contacts.map((c) => ({
                value: c.id,
                label: `${c.name}${c.title ? ` (${c.title})` : ""}${c.booking_contact ? " · books" : ""}${c.do_not_call ? " · do not call" : ""}${c.current ? "" : " · former"}`,
              })),
            ]}
          />
        )}
        <NativeSelect
          label={
            review && suggestedDisposition
              ? "How it ended · suggested"
              : "How it ended"
          }
          value={data.disposition}
          onChange={(e) => {
            const value = e.target.value
            setData((d) => ({
              ...d,
              disposition: value,
              // The hand form infers who was reached from the outcome; the
              // review screen already knows, from the session.
              reached: review
                ? d.reached
                : reachedFor(dispositions.find((x) => x.value === value)),
            }))
          }}
          hint={dispositionHint}
          disabled={disabled}
          options={[{ label: "Pick one", value: "" }, ...dispositionOptions]}
        />
      </div>

      <div
        className={
          review
            ? "grid grid-cols-2 gap-4 sm:grid-cols-4"
            : "grid grid-cols-1 gap-4 sm:grid-cols-3"
        }
      >
        <NativeSelect
          label="Who answered"
          value={data.reached}
          onChange={(e) => setData("reached", e.target.value)}
          hint={hintOf("reached")}
          disabled={disabled}
          options={reached_options.map((r) => ({
            label: REACHED_LABELS[r] ?? r,
            value: r,
          }))}
        />
        <TextField
          type="number"
          value={data.talk_minutes}
          onChange={(v: string) => setData("talk_minutes", v)}
          isInvalid={!!errors.talk_minutes}
          error={errorOf("talk_minutes")}
          isDisabled={disabled}
        >
          <Label>{review ? "Talk (min)" : "Minutes on the call"}</Label>
          <InputBase size="md" {...{ min: 0, max: 599 }} />
        </TextField>
        <TextField
          type="datetime-local"
          value={data.called_at}
          onChange={(v: string) => setData("called_at", v)}
          isInvalid={!!errors.called_at}
          error={errorOf("called_at")}
          isDisabled={disabled}
        >
          <Label>{review ? "Called at" : "When"}</Label>
          <InputBase size="md" />
        </TextField>
        {review && (
          <div className="flex flex-col gap-1.5">
            <span className="text-secondary text-sm font-medium">Attempt</span>
            <span className="notation text-primary flex h-8 items-center">
              {attempt.step} of {attempt.of}
            </span>
          </div>
        )}
      </div>

      <Checkbox
        isSelected={data.via_reception}
        onChange={(v: boolean) => setData("via_reception", v)}
        isDisabled={disabled}
        label="Went through reception"
      />

      {asks === "commitment" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NativeSelect
            label="Which cohort they booked for"
            value={data.cohort_name}
            onChange={(e) => setData("cohort_name", e.target.value)}
            hint={
              hintOf("cohort_name") ??
              "Opens a verbal booking the nightly matcher closes when the job appears."
            }
            disabled={disabled}
            options={[
              { label: "Pick one", value: "" },
              ...cohorts.map((k) => ({ value: k.name, label: cohortLabel(k) })),
            ]}
          />
          <NativeSelect
            label="For which term (if they said)"
            value={data.expected_term}
            onChange={(e) => setData("expected_term", e.target.value)}
            hint={hintOf("expected_term")}
            disabled={disabled}
            options={[
              { label: "Not said", value: "" },
              ...terms.map((t) => ({ value: t.value, label: t.label })),
            ]}
          />
        </div>
      )}
      {asks === "provider" && (
        <TextField
          value={data.provider}
          onChange={(v: string) => setData("provider", v)}
          isInvalid={!!errors.provider}
          error={errorOf("provider")}
          isDisabled={disabled}
        >
          <Label>Who they use instead</Label>
          <InputBase
            size="md"
            placeholder="The provider's name as they said it"
          />
        </TextField>
      )}
      {asks === "consent" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_180px]">
          <TextField
            value={data.consent_method}
            onChange={(v: string) => setData("consent_method", v)}
            isInvalid={!!errors.consent_method}
            error={errorOf("consent_method")}
            isDisabled={disabled}
          >
            <Label>How consent works there</Label>
            <InputBase
              size="md"
              placeholder="Opt-out letter; opt-in form via the parent portal"
            />
          </TextField>
          <TextField
            type="number"
            value={data.consent_lead_days}
            onChange={(v: string) => setData("consent_lead_days", v)}
            isInvalid={!!errors.consent_lead_days}
            error={errorOf("consent_lead_days")}
            isDisabled={disabled}
          >
            <Label>Lead time (days)</Label>
            <InputBase size="md" {...{ min: 0 }} />
          </TextField>
        </div>
      )}
      {asks === "term" && (
        <NativeSelect
          label="The term they would rather book"
          value={data.preferred_term}
          onChange={(e) => setData("preferred_term", e.target.value)}
          hint={
            hintOf("preferred_term") ??
            "The school is off the list until the next season opens."
          }
          disabled={disabled}
          options={[
            { label: "Not said", value: "" },
            ...[1, 2, 3, 4].map((t) => ({
              value: String(t),
              label: `Term ${t}`,
            })),
          ]}
        />
      )}
      {asks === "venue" && (
        <TextArea
          label="Venue and timetable"
          rows={3}
          placeholder="Hall is booked Wednesdays; periods are 50 minutes; use the library."
          value={data.venue_notes}
          onChange={(v: string) => setData("venue_notes", v)}
          isInvalid={!!errors.venue_notes}
          isDisabled={disabled}
          hint={hintOf("venue_notes")}
        />
      )}
      {asks !== "commitment" && data.salesforce_contact_id && (
        <NativeSelect
          label="Which cohort they book for (optional)"
          value={data.cohort_name}
          onChange={(e) => setData("cohort_name", e.target.value)}
          hint={hintOf("cohort_name")}
          disabled={disabled}
          options={[
            { label: "Not said", value: "" },
            ...cohorts.map((k) => ({ value: k.name, label: cohortLabel(k) })),
          ]}
        />
      )}

      <div id="review-notes" className="flex flex-col gap-1">
        {notesNote && <span className="notation">{notesNote}</span>}
        <TextArea
          label={
            chosen?.spoke ? "What they said" : "Notes (optional for an attempt)"
          }
          rows={5}
          placeholder={
            notesPlaceholder ??
            "What was agreed, what they pushed back on, who else to talk to."
          }
          value={data.notes}
          onChange={(v: string) => setData("notes", v)}
          isInvalid={!!errors.notes}
          isDisabled={disabled}
          hint={hintOf("notes")}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_180px]">
        <TextField
          value={data.next_step}
          onChange={(v: string) => setData("next_step", v)}
          isInvalid={!!errors.next_step}
          error={errorOf("next_step")}
          isDisabled={disabled}
        >
          <Label>{review ? "Follow-up" : "Next step (optional)"}</Label>
          <InputBase
            size="md"
            placeholder="Send the consent pack; call back after the exec meeting"
          />
        </TextField>
        <TextField
          type="date"
          value={data.next_step_on}
          onChange={(v: string) => setData("next_step_on", v)}
          isInvalid={!!errors.next_step_on}
          error={errorOf("next_step_on")}
          isDisabled={disabled}
        >
          <Label>By</Label>
          <InputBase size="md" {...{ min: today() }} />
        </TextField>
      </div>
      <p className="text-tertiary text-xs">
        A next step becomes an open follow-up Task on the account. A call back
        with no date is due in five business days.
      </p>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {footer ??
          (review && (
            <span className="text-tertiary mr-auto text-xs">
              {REVIEW.writesOne}
            </span>
          ))}
        {cancelHref && (
          <Button size="md" color="secondary" href={cancelHref}>
            Cancel
          </Button>
        )}
        <Button
          size={review ? "lg" : "md"}
          color="primary"
          type="submit"
          isLoading={processing}
          isDisabled={disabled || !data.disposition}
          className="pointer-coarse:h-11"
        >
          {processing ? PRESSED_ECHO.log : submitLabel}
        </Button>
      </div>
    </form>
  )
}
